import type { SynonymRule, WidgetFold } from '../create-widget-normalizer';
import { isBoolean, isNumber, isString, parseIfJsonText } from '../fold-helpers';

/**
 * The synonym rules and bespoke folds the subscription form and the payment form share — the third
 * shared piece, alongside the schema shape and the patch fold, and shared for the same reason.
 *
 * The synonym lists are the persisted names as much as the colloquial ones: `formStyleType`,
 * `subscriptionMessage`, `openLink` and `buttonStyleType` are what the ENTITY calls these fields, so
 * a model that has just read a widget out of `get_page_snapshot` will reach for them rather than for
 * the flat surface. Accepting them costs a line each; refusing them costs a round trip.
 */

const FIELDS_SYNONYM_KEYS = ['elements', 'inputs', 'formFields'] as const;
const BUTTON_LABEL_SYNONYM_KEYS = ['submitLabel', 'buttonText', 'submitText'] as const;
const SUCCESS_MESSAGE_SYNONYM_KEYS = ['confirmMessage', 'thankYouMessage', 'subscriptionMessage'] as const;
const SUCCESS_LINK_SYNONYM_KEYS = ['redirectUrl', 'redirectLink', 'openLink'] as const;
const FORM_STYLE_SYNONYM_KEYS = ['formStyleType', 'layout'] as const;
const FORM_VIEW_SYNONYM_KEYS = ['view'] as const;
const SHOW_LABELS_SYNONYM_KEYS = ['labels'] as const;
const BUTTON_STYLE_SYNONYM_KEYS = ['buttonStyleType'] as const;

/**
 * `fields` arrives as JSON TEXT down the leaked-function-calls path, where every argument is a
 * string (`fold-helpers.parseIfJsonText`, and the three observed round trips that motivated it). The
 * synonym rule guards on `Array.isArray`, so an unparsed string would be refused before it ever
 * reached the schema — parse first, under every name it may arrive under.
 */
export const parseFieldsText: WidgetFold = (args, data) => {
    for (const key of ['fields', ...FIELDS_SYNONYM_KEYS] as const) {
        if (data[key] !== undefined) {
            data[key] = parseIfJsonText(data[key]);
        }

        if (args[key] !== undefined) {
            args[key] = parseIfJsonText(args[key]);
        }
    }
};

/**
 * A bare list of kinds — `["email", "name"]` — expanded into entries.
 *
 * Not a courtesy: `fields` is the most substantial field of this surface, and asked for objects a
 * model reaches for the shorthand often enough that the alternative is a hard schema failure on an
 * otherwise perfectly clear call. The labels then come from nothing, which is correct: an unlabelled
 * field is the editor's own default state, whereas an invented English label on a Russian page is a
 * defect nobody asked for.
 */
export const expandShorthandFields: WidgetFold = (_args, data) => {
    const fields = data['fields'];

    if (!Array.isArray(fields)) {
        return;
    }

    data['fields'] = fields.map((entry) => (typeof entry === 'string' ? { type: entry } : entry));
};

/**
 * `buttons: [{ label }]` lifted onto the flat `buttonLabel`.
 *
 * Observed live 2026-08-28: asked for a form, the model sent
 * `data: { buttons: [{ label: "Подписаться" }] }` — the BUTTON widget's shape, which
 * `create_button_widget` documents and folds the other way round (flat `label` INTO the array). A
 * model that has just read that tool generalises it, and a plain `z.object()` would strip the key
 * silently, leaving the submit button unlabelled while the call reports success. One round trip
 * lost per occurrence, so accept the shape and move the value where this widget keeps it.
 */
export const foldButtonsWrapper: WidgetFold = (_args, data) => {
    const buttons = data['buttons'];

    if (!Array.isArray(buttons)) {
        return;
    }

    const first = buttons[0];

    if (data['buttonLabel'] === undefined && first != null && typeof first === 'object') {
        const label = (first as Record<string, unknown>)['label'];

        if (typeof label === 'string') {
            data['buttonLabel'] = label;
        }
    }

    delete data['buttons'];
};

export const FORM_APPEARANCE_RULES: readonly SynonymRule[] = [
    { target: 'fields', synonyms: FIELDS_SYNONYM_KEYS, accepts: Array.isArray },
    { target: 'buttonLabel', synonyms: BUTTON_LABEL_SYNONYM_KEYS, accepts: isString },
    { target: 'buttonStyle', synonyms: BUTTON_STYLE_SYNONYM_KEYS, accepts: isString },
    { target: 'successMessage', synonyms: SUCCESS_MESSAGE_SYNONYM_KEYS, accepts: isString },
    { target: 'successLink', synonyms: SUCCESS_LINK_SYNONYM_KEYS, accepts: isString },
    { target: 'formStyle', synonyms: FORM_STYLE_SYNONYM_KEYS, accepts: isString },
    { target: 'formView', synonyms: FORM_VIEW_SYNONYM_KEYS, accepts: isString },
    { target: 'showLabels', synonyms: SHOW_LABELS_SYNONYM_KEYS, accepts: isBoolean },
    { target: 'width', synonyms: [], accepts: isNumber },
];
